const courseworkProjects = {
    '/coursework/unix-shell': {
        label: 'Private coursework · Purdue CS 252 · C++ · Lex/Yacc',
        shortTitle: 'Unix Shell',
        kicker: 'Private coursework · Purdue CS 252',
        title: 'A Unix shell from parsing to process control.',
        lede: 'A command parser built with Lex and Yacc, process pipelines with fork and exec, I/O redirection, jobs, expansion, and control flow.',
        meta: [['Course', 'CS 252'], ['Language', 'C++'], ['Tools', 'Lex + Yacc']],
        pipeline: [
            { label: 'Read input', sub: 'command line' },
            { label: 'Parse', sub: 'lex · yacc' },
            { label: 'Expand', sub: 'env · wildcard' },
            { label: 'Fork + exec', sub: 'pipeline' },
            { label: 'Redirect', sub: 'file descriptors' },
            { label: 'Jobs', sub: 'foreground · bg' },
        ],
        mechanisms: [
            {
                name: 'Command parser',
                symbols: ['lex', 'yacc'],
                description: 'Converted shell syntax into structured commands before execution.',
            },
            {
                name: 'Process pipelines',
                symbols: ['fork', 'exec'],
                description: 'Created process pipelines with fork and exec, connecting stages into running commands.',
            },
            {
                name: 'I/O redirection',
                symbols: ['file descriptors'],
                description: 'Connected file descriptors so commands read and write files instead of the terminal.',
            },
            {
                name: 'Job control',
                symbols: ['foreground', 'background', 'signals'],
                description: 'Supported foreground and background jobs with signal handling.',
            },
            {
                name: 'Expansion',
                symbols: ['environment', 'wildcard', 'subshell'],
                description: 'Implemented environment and wildcard expansion, plus subshells.',
            },
            {
                name: 'Control flow',
                symbols: ['if', 'while', 'for'],
                description: 'Added if, while, and for control flow to the shell language.',
            },
        ],
        paragraphs: [
            {
                id: 'parser',
                content: (
                    <>
                        I built a command parser with <strong>Lex and Yacc</strong> that converted shell syntax into
                        structured commands before execution.
                    </>
                ),
            },
            {
                id: 'execution',
                content: (
                    <>
                        The shell created process pipelines with fork and exec, connected file descriptors for
                        <strong> I/O redirection</strong>, and supported foreground and background jobs.
                    </>
                ),
            },
            {
                id: 'features',
                content: (
                    <>
                        I also implemented environment and wildcard expansion, subshells, signal handling, and
                        <strong> if, while, and for</strong> control flow.
                    </>
                ),
            },
        ],
        footer: 'Private coursework · Solution code is not distributed',
        links: [
            {
                label: 'Read Purdue’s shell chapter',
                href: 'https://www.cs.purdue.edu/homes/grr/SystemsProgrammingBook/Book/Chapter5-WritingYourOwnShell.pdf',
            },
        ],
    },
    '/coursework/operating-systems': {
        label: 'Private coursework · Purdue CS 354 · C',
        title: 'Working inside an operating system.',
        paragraphs: [
            {
                id: 'overview',
                content: (
                    <>
                        Across five labs in <strong>Purdue CS 354</strong>, I worked inside Xinu, a compact teaching
                        operating system written in C, instead of building on top of an existing runtime.
                    </>
                ),
            },
            {
                id: 'environment',
                content: (
                    <>
                        I developed and tested kernels using <strong>Purdue’s dedicated Xinu lab machines</strong>,
                        working below the application layer in an environment maintained specifically for operating
                        systems coursework.
                    </>
                ),
            },
            {
                id: 'kernel-work',
                content: (
                    <>
                        My implementations extended process creation and <strong>inter-process communication</strong>,
                        including per-process state and new send and receive paths. That required coordinated changes
                        across kernel headers, process tables, initialization, and system-call implementations.
                    </>
                ),
            },
            {
                id: 'takeaway',
                content: (
                    <>
                        Working in a small kernel made the machinery behind processes, message passing, and kernel
                        execution concrete—and made every state transition matter.
                    </>
                ),
            },
        ],
        footer: 'Private coursework · Solution code is not distributed',
        links: [
            {
                label: 'Explore the public Xinu project',
                href: 'https://github.com/xinu-os/xinu',
            },
            {
                label: 'Read about Xinu at Purdue',
                href: 'https://xinu.cs.purdue.edu/',
            },
        ],
    },
    '/coursework/ai-search-agents': {
        label: 'Private coursework · Purdue CS 471 · Python',
        shortTitle: 'AI Search Agents',
        kicker: 'Private coursework · Purdue CS 471',
        title: 'Search and game-playing agents.',
        lede: 'Uninformed and heuristic search plus adversarial planning in Python — from frontiers to game trees.',
        meta: [['Course', 'CS 471'], ['Language', 'Python'], ['Material', 'Berkeley AI']],
        pipeline: [
            { label: 'Start', sub: 'initial state' },
            { label: 'Frontier', sub: 'open · explored' },
            { label: 'Expand', sub: 'costs · heuristics' },
            { label: 'Goal test', sub: 'found?' },
            { label: 'Path', sub: 'actions back' },
        ],
        mechanisms: [
            {
                name: 'Uninformed search',
                symbols: ['dfs', 'bfs', 'ucs'],
                description: 'Depth-first, breadth-first, and uniform-cost search with frontier management.',
            },
            {
                name: 'A* search',
                symbols: ['a*', 'heuristic'],
                description: 'A* with path costs, explored-state tracking, and heuristics.',
            },
            {
                name: 'Minimax',
                symbols: ['minimax'],
                description: 'Adversarial search evaluating future states to select actions.',
            },
            {
                name: 'Alpha-beta pruning',
                symbols: ['alpha-beta'],
                description: 'Pruning that cuts search while keeping the minimax decision.',
            },
            {
                name: 'Expectimax',
                symbols: ['expectimax'],
                description: 'Action selection under uncertainty about other agents.',
            },
            {
                name: 'Tradeoffs',
                symbols: ['optimality', 'memory'],
                description: 'Optimality, memory use, heuristic quality, pruning, and depth — compared through working agents.',
            },
        ],
        paragraphs: [
            {
                id: 'search',
                content: (
                    <>
                        I implemented <strong>depth-first, breadth-first, uniform-cost, and A* search</strong> in
                        Python, including frontier management, explored-state tracking, path costs, and heuristics.
                    </>
                ),
            },
            {
                id: 'games',
                content: (
                    <>
                        For adversarial and uncertain environments, I built <strong>minimax, alpha-beta pruning, and
                        expectimax</strong> agents that evaluated future states and selected actions under different
                        assumptions about other agents.
                    </>
                ),
            },
            {
                id: 'tradeoffs',
                content: (
                    <>
                        The projects made the tradeoffs between optimality, memory use, heuristic quality, pruning,
                        and search depth concrete through working agents rather than isolated algorithm exercises.
                    </>
                ),
            },
        ],
        footer: 'Private coursework · Solution code is not distributed',
        links: [
            {
                label: 'Explore Berkeley AI’s search material',
                href: 'https://github.com/BerkeleyAI/textbook/tree/main/search',
            },
            {
                label: 'Explore Berkeley AI’s game material',
                href: 'https://github.com/BerkeleyAI/textbook/tree/main/games',
            },
        ],
    },
};

export default courseworkProjects;
